import { useEffect, useRef, useState } from 'react';
import { useAuthSafe, useKeycloak } from '../components/ConditionalAuthProvider';
import { isBackendCapabilitySourceEnabled, isKeycloakEnabled } from '../config/authConfig';
import accessRevalidationClock from '../services/accessRevalidationClock';
import currentUserAccessService, {
  CURRENT_USER_ACCESS_PROACTIVE_LEAD_MS,
  CURRENT_USER_ACCESS_RETRY_DELAY_MS,
  CURRENT_USER_ACCESS_TTL_MS,
  CurrentUserAccess,
} from '../services/currentUserAccessService';
import {
  CAPABILITY_CODES,
  extractCapabilitiesFromAuthUser,
  extractCapabilitiesFromToken,
  extractRealmRolesFromToken,
  extractRolesFromAuthUser,
  hasAdminRole,
  hasCapabilityCode,
} from '../utils/capabilityAccess';

type BackendAccessState = {
  loading: boolean;
  user: CurrentUserAccess | null;
  error: unknown | null;
  identityKey: string | null;
  expiresAtMs: number | null;
};

const emptyBackendAccessState: BackendAccessState = {
  loading: false,
  user: null,
  error: null,
  identityKey: null,
  expiresAtMs: null,
};

const expiredEvidenceError = new Error('current-user evidence expired');
const sessionInvalidatedError = new Error('current-user session invalidated');

const readAccountIdentity = (
  keycloakMode: boolean,
  keycloakState: ReturnType<typeof useKeycloak>,
  auth: ReturnType<typeof useAuthSafe>
): string => {
  if (keycloakMode) {
    const tokenParsed = keycloakState.keycloak?.tokenParsed as { sub?: unknown } | undefined;
    return String(tokenParsed?.sub ?? '');
  }
  return String(auth.user?.id ?? '');
};

const readInitialBackendAccess = (identityKey: string): BackendAccessState => {
  const retryAfterMs = currentUserAccessService.getRetryAfterMs?.() ?? 0;
  if (retryAfterMs > 0) {
    return {
      loading: false,
      user: null,
      error: new Error('current-user request backing off after a prior failure'),
      identityKey,
      expiresAtMs: null,
    };
  }
  const cached = currentUserAccessService.peek?.(identityKey) ?? null;
  const expiresAtMs = currentUserAccessService.getExpiresAtMs?.(identityKey)
    ?? (cached ? Date.now() + (currentUserAccessService.getRemainingTtlMs?.(identityKey) ?? 0) : null);
  if (!cached || expiresAtMs === null || expiresAtMs <= Date.now()) {
    return emptyBackendAccessState;
  }
  return { loading: false, user: cached, error: null, identityKey, expiresAtMs };
};

export const useCapabilityAccess = () => {
  const auth = useAuthSafe();
  const keycloakState = useKeycloak();
  const keycloakMode = isKeycloakEnabled();
  const backendCapabilitySource = keycloakMode && isBackendCapabilitySourceEnabled();

  const baseInitialized = keycloakMode ? keycloakState.initialized : auth.initialized;
  const authenticated = keycloakMode
    ? Boolean(keycloakState.keycloak?.authenticated)
    : auth.authenticated;
  const keycloakToken = keycloakState.keycloak?.token;
  const identityKey = `${authenticated ? '1' : '0'}:${readAccountIdentity(keycloakMode, keycloakState, auth)}`;
  const sessionKey = `${identityKey}:${keycloakToken ?? ''}`;

  const permissionLoadEnabledRef = useRef(false);
  const revalidateNowRef = useRef<(() => void) | null>(null);
  const seenSessionKeyRef = useRef<string | null>(null);
  const identityKeyRef = useRef(identityKey);
  identityKeyRef.current = identityKey;
  const evidenceRef = useRef<{ identityKey: string | null; expiresAtMs: number | null }>({
    identityKey: null,
    expiresAtMs: null,
  });
  const [backendAccess, setBackendAccess] = useState<BackendAccessState>(() => readInitialBackendAccess(identityKey));

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const onLogout = () => {
      permissionLoadEnabledRef.current = false;
      evidenceRef.current = { identityKey: null, expiresAtMs: null };
      currentUserAccessService.invalidate?.();
      setBackendAccess({
        loading: false,
        user: null,
        error: sessionInvalidatedError,
        identityKey: identityKeyRef.current,
        expiresAtMs: null,
      });
    };
    window.addEventListener('dms-auth-logout', onLogout);
    return () => window.removeEventListener('dms-auth-logout', onLogout);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const onResume = () => {
      if (document.visibilityState === 'visible') {
        revalidateNowRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
    };
  }, []);

  // Keycloak-token / local-auth modes have no backend evidence-refresh cycle
  // to key accessEvidenceVersion off of (see below), so this subscribes to
  // the shared accessRevalidationClock instead, only in those modes - backend
  // mode keeps its own expiresAtMs-driven cycle and must not also subscribe
  // here, or evidence would be revalidated by two independent timers.
  const [accessRevalidationEpoch, setAccessRevalidationEpoch] = useState(() => accessRevalidationClock.getEpoch());
  useEffect(() => {
    if (backendCapabilitySource || !authenticated) {
      return undefined;
    }
    // Pick up any tick(s) the clock advanced while this hook wasn't
    // subscribed (e.g. between an unmount and a remount).
    setAccessRevalidationEpoch(accessRevalidationClock.getEpoch());
    return accessRevalidationClock.subscribe(() => {
      setAccessRevalidationEpoch(accessRevalidationClock.getEpoch());
    });
  }, [backendCapabilitySource, authenticated]);

  useEffect(() => {
    const previousSessionKey = seenSessionKeyRef.current;
    seenSessionKeyRef.current = sessionKey;

    if (!backendCapabilitySource || !baseInitialized || !authenticated) {
      permissionLoadEnabledRef.current = false;
      evidenceRef.current = { identityKey: null, expiresAtMs: null };
      if (!authenticated) {
        currentUserAccessService.invalidate?.();
      }
      setBackendAccess(emptyBackendAccessState);
      return;
    }

    currentUserAccessService.syncSession?.(identityKey);
    permissionLoadEnabledRef.current = true;
    let cancelled = false;
    let deadlineTimerId: number | undefined;
    let attemptTimerId: number | undefined;

    const clearDeadlineTimer = () => {
      if (deadlineTimerId !== undefined) {
        window.clearTimeout(deadlineTimerId);
        deadlineTimerId = undefined;
      }
    };
    const clearAttemptTimer = () => {
      if (attemptTimerId !== undefined) {
        window.clearTimeout(attemptTimerId);
        attemptTimerId = undefined;
      }
    };

    const readSharedExpiresAt = (): number | null => {
      const expiresAtMs = currentUserAccessService.getExpiresAtMs?.(identityKey);
      if (expiresAtMs != null) {
        return expiresAtMs;
      }
      if (!currentUserAccessService.peek?.(identityKey)) {
        return null;
      }
      return Date.now() + (currentUserAccessService.getRemainingTtlMs?.(identityKey) ?? 0);
    };

    const onSettleSuccess = (user: CurrentUserAccess, expiresAtMs: number) => {
      evidenceRef.current = { identityKey, expiresAtMs };
      setBackendAccess({ loading: false, user, error: null, identityKey, expiresAtMs });
      scheduleFromDeadline(expiresAtMs);
    };

    const onSettleFailure = (error: unknown) => {
      console.warn('Failed to load backend capability source', error);
      evidenceRef.current = { identityKey, expiresAtMs: null };
      setBackendAccess({ loading: false, user: null, error, identityKey, expiresAtMs: null });
      scheduleRetry();
    };

    const forceExpireAt = (deadlineMs: number) => {
      if (!permissionLoadEnabledRef.current || evidenceRef.current.expiresAtMs !== deadlineMs) {
        return;
      }
      const cached = currentUserAccessService.peek?.(identityKey);
      const cacheExpiresAtMs = readSharedExpiresAt();
      if (cached && cacheExpiresAtMs !== null && cacheExpiresAtMs > deadlineMs) {
        onSettleSuccess(cached, cacheExpiresAtMs);
        return;
      }
      evidenceRef.current = { identityKey, expiresAtMs: null };
      setBackendAccess((previous) => (previous.identityKey === identityKey
        ? { loading: previous.loading, user: null, error: previous.error ?? expiredEvidenceError, identityKey, expiresAtMs: null }
        : previous));
    };

    const armDeadline = (deadlineMs: number) => {
      clearDeadlineTimer();
      deadlineTimerId = window.setTimeout(() => forceExpireAt(deadlineMs), Math.max(0, deadlineMs - Date.now()));
    };

    const scheduleFromDeadline = (deadlineMs: number) => {
      armDeadline(deadlineMs);
      clearAttemptTimer();
      const untilProactive = Math.max(0, deadlineMs - CURRENT_USER_ACCESS_PROACTIVE_LEAD_MS - Date.now());
      attemptTimerId = window.setTimeout(() => void beginRevalidation(), untilProactive);
    };

    const scheduleRetry = () => {
      clearAttemptTimer();
      const retryAfterMs = currentUserAccessService.getRetryAfterMs?.() ?? 0;
      attemptTimerId = window.setTimeout(
        () => void refreshAccess(),
        retryAfterMs > 0 ? retryAfterMs : CURRENT_USER_ACCESS_RETRY_DELAY_MS
      );
    };

    const refreshAccess = async () => {
      if (!permissionLoadEnabledRef.current) return;
      const retryAfterMs = currentUserAccessService.getRetryAfterMs?.() ?? 0;
      if (retryAfterMs > 0) {
        onSettleFailure(new Error(`current-user request backing off for ${retryAfterMs}ms after a prior failure`));
        return;
      }
      const cached = currentUserAccessService.peek?.(identityKey);
      const cacheExpiresAtMs = readSharedExpiresAt();
      if (cached && cacheExpiresAtMs !== null) {
        onSettleSuccess(cached, cacheExpiresAtMs);
        return;
      }
      evidenceRef.current = { identityKey, expiresAtMs: null };
      setBackendAccess((previous) => {
        const sameIdentity = previous.identityKey === identityKey || previous.identityKey === null;
        if (!sameIdentity) {
          return previous;
        }
        const expired = previous.expiresAtMs !== null && previous.expiresAtMs <= Date.now();
        const keepError = previous.error ?? (expired ? expiredEvidenceError : null);
        return {
          loading: true,
          user: null,
          error: keepError,
          identityKey,
          expiresAtMs: null,
        };
      });
      try {
        const user = await currentUserAccessService.getCurrentUserAccess(identityKey);
        if (cancelled || !permissionLoadEnabledRef.current) return;
        onSettleSuccess(user, readSharedExpiresAt() ?? Date.now() + CURRENT_USER_ACCESS_TTL_MS);
      } catch (error) {
        if (cancelled || !permissionLoadEnabledRef.current) return;
        onSettleFailure(error);
      }
    };

    const beginRevalidation = async () => {
      if (!permissionLoadEnabledRef.current) return;
      const retryAfterMs = currentUserAccessService.getRetryAfterMs?.() ?? 0;
      if (retryAfterMs > 0) {
        onSettleFailure(new Error(`current-user request backing off for ${retryAfterMs}ms after a prior failure`));
        return;
      }
      const myDeadlineMs = evidenceRef.current.expiresAtMs;
      if (myDeadlineMs !== null) {
        armDeadline(myDeadlineMs);
      }
      const cached = currentUserAccessService.peek?.(identityKey);
      const cacheExpiresAtMs = readSharedExpiresAt();
      if (cached && cacheExpiresAtMs !== null && (myDeadlineMs === null || cacheExpiresAtMs > myDeadlineMs)) {
        onSettleSuccess(cached, cacheExpiresAtMs);
        return;
      }
      setBackendAccess((previous) => (previous.identityKey === identityKey ? { ...previous, loading: true } : previous));
      try {
        const user = await currentUserAccessService.forceRefresh(identityKey);
        if (cancelled || !permissionLoadEnabledRef.current) return;
        onSettleSuccess(user, readSharedExpiresAt() ?? Date.now() + CURRENT_USER_ACCESS_TTL_MS);
      } catch (error) {
        if (cancelled || !permissionLoadEnabledRef.current) return;
        onSettleFailure(error);
      }
    };

    const unsubscribe = currentUserAccessService.subscribe?.(() => {
      if (cancelled || !permissionLoadEnabledRef.current) {
        return;
      }
      if (currentUserAccessService.peek?.(identityKey)) {
        return;
      }
      const retryAfterMs = currentUserAccessService.getRetryAfterMs?.() ?? 0;
      if (evidenceRef.current.expiresAtMs !== null) {
        evidenceRef.current = { identityKey, expiresAtMs: null };
        setBackendAccess({
          loading: false,
          user: null,
          error: new Error('current-user evidence invalidated'),
          identityKey,
          expiresAtMs: null,
        });
      }
      if (retryAfterMs > 0) {
        scheduleRetry();
      }
    });

    const canRetainEvidence = backendAccess.identityKey === identityKey
      && backendAccess.user !== null
      && backendAccess.expiresAtMs !== null
      && Date.now() < backendAccess.expiresAtMs;
    const sessionRotated = previousSessionKey !== null && previousSessionKey !== sessionKey;

    if (canRetainEvidence && sessionRotated) {
      evidenceRef.current = { identityKey, expiresAtMs: backendAccess.expiresAtMs };
      void beginRevalidation();
    } else if (canRetainEvidence) {
      evidenceRef.current = { identityKey, expiresAtMs: backendAccess.expiresAtMs };
      scheduleFromDeadline(backendAccess.expiresAtMs as number);
    } else {
      void refreshAccess();
    }

    revalidateNowRef.current = () => {
      const deadline = evidenceRef.current.expiresAtMs;
      if (deadline !== null && Date.now() >= deadline) {
        forceExpireAt(deadline);
      }
      if (!currentUserAccessService.peek?.(identityKey)) {
        void refreshAccess();
      }
    };

    return () => {
      cancelled = true;
      permissionLoadEnabledRef.current = false;
      revalidateNowRef.current = null;
      unsubscribe?.();
      clearDeadlineTimer();
      clearAttemptTimer();
    };
    // backendAccess is read for its latest committed value at effect-start
    // (to decide whether evidence is retainable), not to react to its own
    // updates - including it would re-run this effect on every settle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, backendCapabilitySource, baseInitialized, identityKey, keycloakToken, sessionKey]);

  const now = Date.now();
  const identityMatched = backendAccess.identityKey === identityKey;
  const evidenceExpired = identityMatched
    && backendAccess.expiresAtMs !== null
    && now >= backendAccess.expiresAtMs;
  const effectiveUser = identityMatched
    && backendAccess.expiresAtMs !== null
    && now < backendAccess.expiresAtMs
    ? backendAccess.user
    : null;
  const effectiveError = identityMatched
    ? (backendAccess.error ?? (evidenceExpired ? expiredEvidenceError : null))
    : null;

  const tokenParsed = keycloakState.keycloak?.tokenParsed as any;
  const roles = backendCapabilitySource
    ? extractRolesFromAuthUser(effectiveUser)
    : keycloakMode
      ? extractRealmRolesFromToken(tokenParsed)
      : extractRolesFromAuthUser(auth.user as any);

  const capabilities = backendCapabilitySource
    ? extractCapabilitiesFromAuthUser(effectiveUser)
    : keycloakMode
      ? extractCapabilitiesFromToken(tokenParsed)
      : extractCapabilitiesFromAuthUser(auth.user as any);

  const hasCapability = (capabilityCode: (typeof CAPABILITY_CODES)[keyof typeof CAPABILITY_CODES]) =>
    hasCapabilityCode(capabilities, capabilityCode);

  const adminAccess = hasAdminRole(roles);
  const backendAccessSettled = Boolean(effectiveUser) || Boolean(effectiveError);
  const initialized = baseInitialized && (!backendCapabilitySource || !authenticated || backendAccessSettled);

  const dmsAccess = effectiveUser?.dms ?? { operator: false, administrator: false, accessUnlinkedRtus: false };
  const isDmsOperator = dmsAccess.operator;
  const isDmsAdministrator = dmsAccess.administrator;

  // Readiness signal for consumers (e.g. RTU action hints) that need to
  // revalidate on a fresh permission snapshot rather than on every render of
  // a newly-allocated selection array. In backend-capability-source mode,
  // "fresh" means this hook's evidence actually matches the current identity
  // (identityMatched + a non-expired effectiveUser); expiresAtMs doubles as
  // an evidence-version number since it changes on every successful
  // current-user refresh and is shared across hook instances via
  // currentUserAccessService. In Keycloak-token / local-auth modes there is
  // no backend evidence cycle to key off of, so accessEvidenceVersion instead
  // tracks accessRevalidationEpoch - the shared accessRevalidationClock ticks
  // once every CURRENT_USER_ACCESS_TTL_MS (30s) while at least one consumer
  // is subscribed, giving those modes the same kind of bounded periodic
  // revalidation the backend-evidence cycle provides (without a WebSocket
  // push and without a per-row timer: every mounted consumer shares one
  // interval and one epoch, so a refetch it drives, e.g. RTU action hints,
  // batches into one request no matter how many rows are mounted).
  // accessEvidenceVersion must not be null there while authenticated, or
  // hints would never load.
  const hasFreshBackendEvidence = backendCapabilitySource
    && authenticated
    && backendAccess.identityKey === identityKey
    && effectiveUser !== null;
  const accessIdentityKey = backendCapabilitySource
    ? (hasFreshBackendEvidence ? identityKey : null)
    : (authenticated ? identityKey : null);
  const accessEvidenceVersion = backendCapabilitySource
    ? (hasFreshBackendEvidence ? backendAccess.expiresAtMs : null)
    : (authenticated ? accessRevalidationEpoch : null);

  return {
    initialized,
    authenticated,
    accessIdentityKey,
    accessEvidenceVersion,
    roles,
    capabilities,
    hasCapability,
    hasAdminAccess: adminAccess,
    canAccessInternalObservability:
      hasCapability(CAPABILITY_CODES.INTERNAL_OBSERVABILITY_ACCESS) || adminAccess,
    hasBetaFeatureAccess: hasCapability(CAPABILITY_CODES.FEATURE_BETA_ACCESS),
    canAccessBetaFeatures: hasCapability(CAPABILITY_CODES.FEATURE_BETA_ACCESS),
    hasMasterReadAll: hasCapability(CAPABILITY_CODES.RTU_MASTER_READ_ALL),
    isDmsOperator,
    isDmsAdministrator,
    dmsAccessUnlinkedRtus: dmsAccess.accessUnlinkedRtus,
    canUseDmsOperatorFeatures: isDmsOperator || isDmsAdministrator,
    canUseDmsActions: isDmsAdministrator,
    canManageDmsSettings: isDmsAdministrator,
  };
};
